import * as i1 from '@angular/common';
import { CommonModule, AsyncPipe, NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, EventEmitter, Component, ChangeDetectionStrategy, Input, Output } from '@angular/core';
import { TUI_FALSE_HANDLER } from '@taiga-ui/cdk/constants';
import { TuiDay, TuiMonth, TuiDayRange, TUI_FIRST_DAY, TUI_LAST_DAY, MIN_YEAR, MAX_YEAR, TuiYear, TuiMonthRange, TUI_LAST_DISPLAYED_DAY } from '@taiga-ui/cdk/date-time';
import { TuiMapperPipe } from '@taiga-ui/cdk/pipes/mapper';
import { tuiCreateToken, tuiProvideOptions, tuiNullableSame, tuiPure, tuiIsNumber } from '@taiga-ui/cdk/utils/miscellaneous';
import { TuiScrollIntoView, TuiScrollbar } from '@taiga-ui/core/components/scrollbar';
import { __decorate } from 'tslib';
import { TuiHovered } from '@taiga-ui/cdk/directives/hovered';
import { TuiLet } from '@taiga-ui/cdk/directives/let';
import { TuiRepeatTimes } from '@taiga-ui/cdk/directives/repeat-times';
import { TuiCalendarSheetPipe, TuiOrderWeekDaysPipe, TuiMonthPipe } from '@taiga-ui/core/pipes';
import { TUI_SHORT_WEEK_DAYS, TUI_DAY_TYPE_HANDLER } from '@taiga-ui/core/tokens';
import { TuiLink } from '@taiga-ui/core/components/link';
import { TuiSpinButton } from '@taiga-ui/core/components/spin-button';
const _c0 = (a0, a1, a2, a3, a4) => [a0, a1, a2, a3, a4];
function TuiCalendarSheet_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 3);
  }
  if (rf & 2) {
    const day_r1 = ctx.$implicit;
    i0.ɵɵproperty("textContent", day_r1);
  }
}
function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_div_3_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 11);
  }
  if (rf & 2) {
    const markers_r5 = i0.ɵɵnextContext().ngIf;
    i0.ɵɵstyleProp("background", (markers_r5 == null ? null : markers_r5[1]) || "");
  }
}
function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_div_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10);
    i0.ɵɵelement(1, "div", 11);
    i0.ɵɵtemplate(2, TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_div_3_div_2_Template, 1, 2, "div", 12);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const markers_r5 = ctx.ngIf;
    i0.ɵɵadvance();
    i0.ɵɵstyleProp("background", markers_r5 == null ? null : markers_r5[0]);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", markers_r5.length > 1);
  }
}
function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵpipe(1, "tuiMapper");
    i0.ɵɵlistener("click", function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_Template_div_click_0_listener() {
      i0.ɵɵrestoreView(_r2);
      const item_r3 = i0.ɵɵnextContext().tuiLet;
      const ctx_r3 = i0.ɵɵnextContext(4);
      return i0.ɵɵresetView(ctx_r3.onItemClick(item_r3));
    })("tuiHoveredChange", function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_Template_div_tuiHoveredChange_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const item_r3 = i0.ɵɵnextContext().tuiLet;
      const ctx_r3 = i0.ɵɵnextContext(4);
      return i0.ɵɵresetView(ctx_r3.onItemHovered($event && item_r3));
    });
    i0.ɵɵtext(2);
    i0.ɵɵtemplate(3, TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_div_3_Template, 3, 3, "div", 9);
    i0.ɵɵpipe(4, "tuiMapper");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r3 = i0.ɵɵnextContext().tuiLet;
    const ctx_r3 = i0.ɵɵnextContext(4);
    i0.ɵɵclassProp("t-cell_disabled", ctx_r3.disabledItemHandler(item_r3))("t-cell_today", ctx_r3.itemIsToday(item_r3))("t-cell_unavailable", ctx_r3.itemIsUnavailable(item_r3));
    i0.ɵɵattribute("data-range", ctx_r3.getItemRange(item_r3))("data-type", i0.ɵɵpipeBind2(1, 10, item_r3, ctx_r3.dayTypeHandler));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", item_r3.day, " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBindV(4, 13, i0.ɵɵpureFunction5(19, _c0, item_r3, ctx_r3.toMarkers, ctx_r3.itemIsToday(item_r3), ctx_r3.getItemRange(item_r3), ctx_r3.markerHandler)));
  }
}
function TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_div_1_Template, 5, 25, "div", 7);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r3 = ctx.tuiLet;
    const ctx_r3 = i0.ɵɵnextContext(4);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", item_r3 && (!ctx_r3.itemIsUnavailable(item_r3) || ctx_r3.showAdjacent));
  }
}
function TuiCalendarSheet_div_4_div_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TuiCalendarSheet_div_4_div_1_ng_container_1_ng_container_1_Template, 2, 1, "ng-container", 2);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const colIndex_r6 = ctx.$implicit;
    const rowIndex_r7 = i0.ɵɵnextContext().$implicit;
    const sheet_r8 = i0.ɵɵnextContext().tuiLet;
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiLet", sheet_r8[rowIndex_r7] == null ? null : sheet_r8[rowIndex_r7][colIndex_r6]);
  }
}
function TuiCalendarSheet_div_4_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtemplate(1, TuiCalendarSheet_div_4_div_1_ng_container_1_Template, 2, 1, "ng-container", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const rowIndex_r7 = ctx.$implicit;
    const sheet_r8 = i0.ɵɵnextContext().tuiLet;
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiRepeatTimesOf", (sheet_r8[rowIndex_r7] == null ? null : sheet_r8[rowIndex_r7].length) || 0);
  }
}
function TuiCalendarSheet_div_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, TuiCalendarSheet_div_4_div_1_Template, 2, 1, "div", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const sheet_r8 = ctx.tuiLet;
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiRepeatTimesOf", sheet_r8.length);
  }
}
function TuiCalendarSpin_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r1.value.formattedYear, " ");
  }
}
function TuiCalendarSpin_ng_template_5_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 3);
    i0.ɵɵlistener("click", function TuiCalendarSpin_ng_template_5_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onYearClick());
    });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r1.value.formattedYear, " ");
  }
}
function TuiCalendarYear_div_0_ng_container_1_div_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵlistener("click", function TuiCalendarYear_div_0_ng_container_1_div_1_Template_div_click_0_listener() {
      const item_r2 = i0.ɵɵrestoreView(_r1).tuiLet;
      const ctx_r2 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r2.yearClick.emit(item_r2));
    })("tuiHoveredChange", function TuiCalendarYear_div_0_ng_container_1_div_1_Template_div_tuiHoveredChange_0_listener($event) {
      const item_r2 = i0.ɵɵrestoreView(_r1).tuiLet;
      const ctx_r2 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r2.onItemHovered($event, item_r2));
    });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r2 = ctx.tuiLet;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵclassProp("t-cell_disabled", ctx_r2.isDisabled(item_r2))("t-cell_today", ctx_r2.itemIsToday(item_r2));
    i0.ɵɵproperty("tuiScrollIntoView", ctx_r2.scrollItemIntoView(item_r2));
    i0.ɵɵattribute("data-range", ctx_r2.getItemRange(item_r2));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r2, " ");
  }
}
function TuiCalendarYear_div_0_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TuiCalendarYear_div_0_ng_container_1_div_1_Template, 2, 7, "div", 3);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const colIndex_r4 = ctx.$implicit;
    const rowIndex_r5 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiLet", ctx_r2.getItem(rowIndex_r5, colIndex_r4));
  }
}
function TuiCalendarYear_div_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵtemplate(1, TuiCalendarYear_div_0_ng_container_1_Template, 2, 1, "ng-container", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiRepeatTimesOf", 4);
  }
}
function TuiCalendar_tui_scrollbar_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tui-scrollbar", 2)(1, "tui-calendar-year", 3);
    i0.ɵɵlistener("yearClick", function TuiCalendar_tui_scrollbar_0_Template_tui_calendar_year_yearClick_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onPickerYearClick($event));
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("initialItem", ctx_r1.month.year)("max", ctx_r1.computedMax.year)("min", ctx_r1.computedMin.year)("value", ctx_r1.value);
  }
}
function TuiCalendar_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tui-calendar-spin", 4);
    i0.ɵɵlistener("valueChange", function TuiCalendar_ng_template_1_Template_tui_calendar_spin_valueChange_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onPaginationValueChange($event));
    })("yearClick", function TuiCalendar_ng_template_1_Template_tui_calendar_spin_yearClick_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onPaginationYearClick());
    });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(1, "tui-calendar-sheet", 5);
    i0.ɵɵpipe(2, "tuiMapper");
    i0.ɵɵlistener("dayClick", function TuiCalendar_ng_template_1_Template_tui_calendar_sheet_dayClick_1_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onDayClick($event));
    })("hoveredItemChange", function TuiCalendar_ng_template_1_Template_tui_calendar_sheet_hoveredItemChange_1_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onHoveredItemChange($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("max", ctx_r1.computedMaxViewedMonth)("min", ctx_r1.computedMinViewedMonth)("value", ctx_r1.month);
    i0.ɵɵadvance();
    i0.ɵɵproperty("disabledItemHandler", i0.ɵɵpipeBind4(2, 9, ctx_r1.disabledItemHandler, ctx_r1.disabledItemHandlerMapper, ctx_r1.computedMin, ctx_r1.computedMax))("hoveredItem", ctx_r1.hoveredItem)("markerHandler", ctx_r1.markerHandler)("month", ctx_r1.month)("showAdjacent", ctx_r1.showAdjacent)("value", ctx_r1.value);
  }
}
const TUI_CALENDAR_SHEET_DEFAULT_OPTIONS = {
  rangeMode: false
};
const TUI_CALENDAR_SHEET_OPTIONS = tuiCreateToken(TUI_CALENDAR_SHEET_DEFAULT_OPTIONS);
function tuiCalendarSheetOptionsProvider(options) {
  return tuiProvideOptions(TUI_CALENDAR_SHEET_OPTIONS, options, TUI_CALENDAR_SHEET_DEFAULT_OPTIONS);
}
let TuiCalendarSheet = /*#__PURE__*/(() => {
  class TuiCalendarSheet {
    constructor() {
      this.options = inject(TUI_CALENDAR_SHEET_OPTIONS);
      this.today = TuiDay.currentLocal();
      this.unorderedWeekDays$ = inject(TUI_SHORT_WEEK_DAYS);
      this.dayTypeHandler = inject(TUI_DAY_TYPE_HANDLER);
      this.month = TuiMonth.currentLocal();
      this.disabledItemHandler = TUI_FALSE_HANDLER;
      this.markerHandler = null;
      this.value = null;
      this.hoveredItem = null;
      this.showAdjacent = true;
      /**
       * @deprecated use static DI options instead
       * ```
       * tuiCalendarSheetOptionsProvider({rangeMode: true})
       * ```
       * TODO(v5): delete it
       */
      this.single = true;
      this.hoveredItemChange = new EventEmitter();
      this.dayClick = new EventEmitter();
      this.toMarkers = (day, today, range, markerHandler) => {
        if (today || ['active', 'end', 'start'].includes(range || '')) {
          return null;
        }
        const markers = markerHandler?.(day);
        return markers?.length ? markers : null;
      };
    }
    /**
     * @deprecated TODO(v5): delete it. It is used nowhere except unit tests
     */
    itemIsInterval(day) {
      const {
        value,
        hoveredItem
      } = this;
      if (!(value instanceof TuiDayRange)) {
        return false;
      }
      if (!value.isSingleDay) {
        return value.from.daySameOrBefore(day) && value.to.dayAfter(day);
      }
      if (hoveredItem === null) {
        return false;
      }
      const range = TuiDayRange.sort(value.from, hoveredItem);
      return range.from.daySameOrBefore(day) && range.to.dayAfter(day);
    }
    onItemHovered(item) {
      this.updateHoveredItem(item || null);
    }
    getItemRange(item) {
      const {
        value,
        hoveredItem
      } = this;
      if (!value) {
        return null;
      }
      if (value instanceof TuiDay && !this.computedRangeMode) {
        return value.daySame(item) ? 'active' : null;
      }
      if (value instanceof TuiDayRange && value.isSingleDay) {
        return value.from.daySame(item) ? 'active' : null;
      }
      if (!(value instanceof TuiDay) && !(value instanceof TuiDayRange)) {
        return value.find(day => day.daySame(item)) ? 'active' : null;
      }
      const range = this.getRange(value, hoveredItem);
      if (range.isSingleDay && range.from.daySame(item)) {
        return 'active';
      }
      if (range.from.daySame(item)) {
        return 'start';
      }
      if (range.to.daySame(item)) {
        return 'end';
      }
      return range.from.dayBefore(item) && range.to.dayAfter(item) ? 'middle' : null;
    }
    get computedRangeMode() {
      return !this.single || this.options.rangeMode;
    }
    get isRangePicking() {
      return this.computedRangeMode ? this.value instanceof TuiDay :
      /**
       * Only for backward compatibility!
       * TODO(v5): replace with `this.options.rangeMode && this.value instanceof TuiDay`
       */
      this.value instanceof TuiDayRange && this.value.isSingleDay;
    }
    itemIsToday(item) {
      return this.today.daySame(item);
    }
    itemIsUnavailable(item) {
      return !this.month.monthSame(item);
    }
    onItemClick(item) {
      this.dayClick.emit(item);
    }
    getRange(value, hoveredItem) {
      if (value instanceof TuiDay) {
        return TuiDayRange.sort(value, hoveredItem ?? value);
      }
      return value.isSingleDay ? TuiDayRange.sort(value.from, hoveredItem ?? value.to) : value;
    }
    updateHoveredItem(day) {
      if (tuiNullableSame(this.hoveredItem, day, (a, b) => a.daySame(b))) {
        return;
      }
      this.hoveredItem = day;
      this.hoveredItemChange.emit(day);
    }
    static {
      this.ɵfac = function TuiCalendarSheet_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiCalendarSheet)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiCalendarSheet,
        selectors: [["tui-calendar-sheet"]],
        hostVars: 2,
        hostBindings: function TuiCalendarSheet_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("_picking", ctx.isRangePicking);
          }
        },
        inputs: {
          month: "month",
          disabledItemHandler: "disabledItemHandler",
          markerHandler: "markerHandler",
          value: "value",
          hoveredItem: "hoveredItem",
          showAdjacent: "showAdjacent",
          single: "single"
        },
        outputs: {
          hoveredItemChange: "hoveredItemChange",
          dayClick: "dayClick"
        },
        decls: 6,
        vars: 9,
        consts: [[1, "t-row", "t-row_weekday"], ["class", "t-cell", 3, "textContent", 4, "ngFor", "ngForOf"], [4, "tuiLet"], [1, "t-cell", 3, "textContent"], ["automation-id", "tui-calendar-sheet__row", "class", "t-row", 4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-calendar-sheet__row", 1, "t-row"], [4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-calendar-sheet__cell", "class", "t-cell", 3, "t-cell_disabled", "t-cell_today", "t-cell_unavailable", "click", "tuiHoveredChange", 4, "ngIf"], ["automation-id", "tui-calendar-sheet__cell", 1, "t-cell", 3, "click", "tuiHoveredChange"], ["class", "t-dots", 4, "ngIf"], [1, "t-dots"], [1, "t-dot"], ["class", "t-dot", 3, "background", 4, "ngIf"]],
        template: function TuiCalendarSheet_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵtemplate(1, TuiCalendarSheet_div_1_Template, 1, 1, "div", 1);
            i0.ɵɵpipe(2, "tuiOrderWeekDays");
            i0.ɵɵpipe(3, "async");
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(4, TuiCalendarSheet_div_4_Template, 2, 1, "div", 2);
            i0.ɵɵpipe(5, "tuiCalendarSheet");
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(3, 4, i0.ɵɵpipeBind1(2, 2, ctx.unorderedWeekDays$)));
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("tuiLet", i0.ɵɵpipeBind2(5, 6, ctx.month, true));
          }
        },
        dependencies: [CommonModule, i1.NgForOf, i1.NgIf, i1.AsyncPipe, TuiCalendarSheetPipe, TuiHovered, TuiLet, TuiMapperPipe, TuiOrderWeekDaysPipe, TuiRepeatTimes],
        styles: [".t-row[_ngcontent-%COMP%]{display:flex;justify-content:flex-start;font:var(--tui-font-text-m)}.t-row[_ngcontent-%COMP%]:last-child{justify-content:flex-start}.t-cell[_ngcontent-%COMP%]{position:relative;display:flex;align-items:center;justify-content:center;line-height:2rem;isolation:isolate;cursor:pointer;overflow:hidden;border:.125rem solid transparent;box-sizing:border-box;-webkit-mask:linear-gradient(transparent calc(50% - 1rem),#000 calc(50% - 1rem),#000 calc(50% + 1rem),transparent calc(50% + 1rem));mask:linear-gradient(transparent calc(50% - 1rem),#000 calc(50% - 1rem),#000 calc(50% + 1rem),transparent calc(50% + 1rem))}.t-cell[_ngcontent-%COMP%]:first-child{border-inline-start-color:transparent!important}.t-cell[_ngcontent-%COMP%]:last-child{border-inline-end-color:transparent!important}.t-cell[_ngcontent-%COMP%]:before, .t-cell[_ngcontent-%COMP%]:after{position:absolute;top:0;left:0;bottom:0;right:0;content:\"\";z-index:-1;border-radius:var(--tui-radius-m)}.t-cell[_ngcontent-%COMP%]:after{-webkit-mask:url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 12 32\"><path d=\"M0.2856 0L0.6763 0C2.9265 0 4.9876 1.259 6.0147 3.2611L10.2442 11.5048C11.5301 14.0113 11.5683 16.9754 10.3472 19.5141L5.9766 28.6007C4.9772 30.6786 2.8754 32 0.5696 32H0.285645V0Z\"></path></svg>') right / .75rem 100% no-repeat,linear-gradient(#000,#000) left / calc(100% - .7rem) 100% no-repeat;mask:url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 12 32\"><path d=\"M0.2856 0L0.6763 0C2.9265 0 4.9876 1.259 6.0147 3.2611L10.2442 11.5048C11.5301 14.0113 11.5683 16.9754 10.3472 19.5141L5.9766 28.6007C4.9772 30.6786 2.8754 32 0.5696 32H0.285645V0Z\"></path></svg>') right / .75rem 100% no-repeat,linear-gradient(#000,#000) left / calc(100% - .7rem) 100% no-repeat}.t-cell[data-range][_ngcontent-%COMP%]:before{background:var(--tui-background-neutral-1)}._picking[_nghost-%COMP%]   .t-cell[data-range][_ngcontent-%COMP%]:before{background:var(--tui-background-neutral-1-hover)}.t-cell[data-range=middle][_ngcontent-%COMP%]{border-color:var(--tui-background-neutral-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=middle][_ngcontent-%COMP%]{border-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=middle][_ngcontent-%COMP%]:not(:first-child):before{border-top-left-radius:0;border-bottom-left-radius:0}.t-cell[data-range=middle][_ngcontent-%COMP%]:not(:last-child):before{border-top-right-radius:0;border-bottom-right-radius:0}.t-cell[data-range=start][_ngcontent-%COMP%]{border-inline-end-color:var(--tui-background-neutral-1);color:var(--tui-text-primary-on-accent-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=start][_ngcontent-%COMP%]{border-inline-end-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=start][_ngcontent-%COMP%]:not(:last-child):before{right:-1rem}.t-cell[data-range=start][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1)}.t-cell[data-range=end][_ngcontent-%COMP%]{border-inline-start-color:var(--tui-background-neutral-1);color:var(--tui-text-primary-on-accent-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=end][_ngcontent-%COMP%]{border-inline-start-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=end][_ngcontent-%COMP%]:not(:first-child):before{left:-1rem}.t-cell[data-range=end][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1);transform:scaleX(-1)}.t-cell[data-range=active][_ngcontent-%COMP%]{color:var(--tui-text-primary-on-accent-1)}.t-cell[data-range=active][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1);-webkit-mask:none;mask:none}.t-cell_disabled[_ngcontent-%COMP%]{opacity:var(--tui-disabled-opacity);pointer-events:none}.t-cell_today[_ngcontent-%COMP%]{text-decoration:underline;text-underline-offset:.25rem}@media (hover: hover) and (pointer: fine){.t-cell[_ngcontent-%COMP%]:hover:not([data-range=start]):not([data-range=end]):before{background:var(--tui-background-neutral-1-hover)}.t-cell[data-range=start][_ngcontent-%COMP%]:hover:after, .t-cell[data-range=end][_ngcontent-%COMP%]:hover:after, .t-cell[data-range=active][_ngcontent-%COMP%]:hover:after{background:var(--tui-background-accent-1-hover)}}.t-cell[_ngcontent-%COMP%]{inline-size:2.25rem}[data-type=weekday][_ngcontent-%COMP%]{color:var(--tui-text-primary)}[data-type=weekend][_ngcontent-%COMP%]{color:var(--tui-text-negative)}.t-row[_ngcontent-%COMP%]{justify-content:flex-start}.t-row[_ngcontent-%COMP%]:first-child{justify-content:flex-end}.t-row_weekday[_ngcontent-%COMP%]{font:var(--tui-font-text-s);color:var(--tui-text-secondary);pointer-events:none}.t-cell_unavailable[_ngcontent-%COMP%]{opacity:var(--tui-disabled-opacity)}.t-dots[_ngcontent-%COMP%]{position:absolute;bottom:0;display:flex;justify-content:center;margin-top:-.5rem;padding-bottom:.25rem}.t-dot[_ngcontent-%COMP%]{display:inline-block;inline-size:.25rem;block-size:.25rem;border-radius:100%;margin:0 .0625rem}"],
        changeDetection: 0
      });
    }
  }
  __decorate([tuiPure], TuiCalendarSheet.prototype, "getRange", null);
  return TuiCalendarSheet;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiCalendarSpin = /*#__PURE__*/(() => {
  class TuiCalendarSpin {
    constructor() {
      this.value = TuiMonth.currentLocal();
      this.min = TUI_FIRST_DAY;
      this.max = TUI_LAST_DAY;
      this.valueChange = new EventEmitter();
      this.yearClick = new EventEmitter();
    }
    onYearClick() {
      this.yearClick.next(this.value);
    }
    append(date) {
      const value = this.value.append(date);
      if (this.min.monthSameOrAfter(value)) {
        this.updateValue(this.min);
      } else {
        this.updateValue(this.max.monthSameOrBefore(value) ? this.max : value);
      }
    }
    updateValue(value) {
      if (this.value.monthSame(value)) {
        return;
      }
      this.value = value;
      this.valueChange.emit(value);
    }
    static {
      this.ɵfac = function TuiCalendarSpin_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiCalendarSpin)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiCalendarSpin,
        selectors: [["tui-calendar-spin"]],
        inputs: {
          value: "value",
          min: "min",
          max: "max"
        },
        outputs: {
          valueChange: "valueChange",
          yearClick: "yearClick"
        },
        decls: 7,
        vars: 10,
        consts: [["button", ""], [3, "leftClick", "rightClick", "focusable", "leftDisabled", "rightDisabled"], [4, "ngIf", "ngIfElse"], ["id", "year-btn", "automation-id", "tui-primitive-year-month-pagination__year-button", "tabIndex", "-1", "tuiLink", "", "type", "button", 3, "click"]],
        template: function TuiCalendarSpin_Template(rf, ctx) {
          if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "tui-spin-button", 1);
            i0.ɵɵlistener("leftClick", function TuiCalendarSpin_Template_tui_spin_button_leftClick_0_listener() {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.append({
                month: -1
              }));
            })("rightClick", function TuiCalendarSpin_Template_tui_spin_button_rightClick_0_listener() {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.append({
                month: 1
              }));
            });
            i0.ɵɵtext(1);
            i0.ɵɵpipe(2, "tuiMonth");
            i0.ɵɵpipe(3, "async");
            i0.ɵɵtemplate(4, TuiCalendarSpin_ng_container_4_Template, 2, 1, "ng-container", 2)(5, TuiCalendarSpin_ng_template_5_Template, 2, 1, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            const button_r4 = i0.ɵɵreference(6);
            i0.ɵɵproperty("focusable", false)("leftDisabled", ctx.value.monthSameOrBefore(ctx.min))("rightDisabled", ctx.value.monthSameOrAfter(ctx.max));
            i0.ɵɵadvance();
            i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(3, 8, i0.ɵɵpipeBind1(2, 6, ctx.value)), " ");
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("ngIf", ctx.min.year === ctx.max.year)("ngIfElse", button_r4);
          }
        },
        dependencies: [AsyncPipe, NgIf, TuiLink, TuiMonthPipe, TuiSpinButton],
        styles: ["[_nghost-%COMP%]{display:block}"],
        changeDetection: 0
      });
    }
  }
  return TuiCalendarSpin;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const LIMIT = 100;
const ITEMS_IN_ROW = 4;
let TuiCalendarYear = /*#__PURE__*/(() => {
  class TuiCalendarYear {
    constructor() {
      this.hoveredItem = null;
      this.currentYear = TuiMonth.currentLocal().year;
      this.value = null;
      this.initialItem = this.currentYear;
      this.min = MIN_YEAR;
      this.max = MAX_YEAR;
      this.disabledItemHandler = TUI_FALSE_HANDLER;
      this.yearClick = new EventEmitter();
    }
    isDisabled(item) {
      return this.max && this.max < item || this.min && this.min > item || this.disabledItemHandler(item);
    }
    getItemRange(item) {
      const {
        value,
        hoveredItem
      } = this;
      if (value instanceof TuiYear) {
        return value.year === item ? 'active' : null;
      }
      if (tuiIsNumber(value)) {
        return value === item ? 'active' : null;
      }
      if (!(value instanceof TuiMonthRange)) {
        return value?.find(day => day.year === item) ? 'active' : null;
      }
      const hovered = this.isSingle ? hoveredItem : null;
      const from = Math.min(value.from.year, hovered ?? value.to.year);
      const to = Math.max(value.from.year, hovered ?? value.to.year);
      if (from === to && value.from.year === value.to.year && from === item) {
        return 'active';
      }
      if (from === item) {
        return 'start';
      }
      if (to === item) {
        return 'end';
      }
      return from < item && item < to ? 'middle' : null;
    }
    onItemHovered(hovered, item) {
      this.updateHoveredItem(hovered, item);
    }
    get isSingle() {
      return this.value instanceof TuiMonthRange ? this.value.from.monthSame(this.value.to) : this.value instanceof TuiDayRange && this.value.isSingleDay;
    }
    get rows() {
      return Math.ceil((this.calculatedMax - this.calculatedMin) / ITEMS_IN_ROW);
    }
    scrollItemIntoView(item) {
      return this.initialItem === item;
    }
    getItem(rowIndex, colIndex) {
      return rowIndex * ITEMS_IN_ROW + colIndex + this.calculatedMin;
    }
    itemIsToday(item) {
      return this.currentYear === item;
    }
    get calculatedMin() {
      const initial = this.initialItem - LIMIT;
      const min = this.min ?? MIN_YEAR;
      return min > initial ? min : initial;
    }
    get calculatedMax() {
      const initial = this.initialItem + LIMIT;
      const max = this.max ?? MAX_YEAR;
      return max < initial ? max + 1 : initial;
    }
    updateHoveredItem(hovered, item) {
      this.hoveredItem = hovered ? item : null;
    }
    static {
      this.ɵfac = function TuiCalendarYear_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiCalendarYear)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiCalendarYear,
        selectors: [["tui-calendar-year"]],
        hostVars: 2,
        hostBindings: function TuiCalendarYear_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("_picking", ctx.isSingle);
          }
        },
        inputs: {
          value: "value",
          initialItem: "initialItem",
          min: "min",
          max: "max",
          disabledItemHandler: "disabledItemHandler"
        },
        outputs: {
          yearClick: "yearClick"
        },
        decls: 1,
        vars: 1,
        consts: [["automation-id", "tui-calendar-year__row", "class", "t-row", 4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-calendar-year__row", 1, "t-row"], [4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-calendar-year__cell", "class", "t-cell", 3, "t-cell_disabled", "t-cell_today", "tuiScrollIntoView", "click", "tuiHoveredChange", 4, "tuiLet"], ["automation-id", "tui-calendar-year__cell", 1, "t-cell", 3, "click", "tuiHoveredChange", "tuiScrollIntoView"]],
        template: function TuiCalendarYear_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, TuiCalendarYear_div_0_Template, 2, 1, "div", 0);
          }
          if (rf & 2) {
            i0.ɵɵproperty("tuiRepeatTimesOf", ctx.rows);
          }
        },
        dependencies: [TuiHovered, TuiLet, TuiRepeatTimes, TuiScrollIntoView],
        styles: [".t-row[_ngcontent-%COMP%]{display:flex;justify-content:flex-start;font:var(--tui-font-text-m)}.t-row[_ngcontent-%COMP%]:first-child{justify-content:flex-end}.t-row[_ngcontent-%COMP%]:last-child{justify-content:flex-start}.t-cell[_ngcontent-%COMP%]{position:relative;display:flex;align-items:center;justify-content:center;line-height:2rem;isolation:isolate;cursor:pointer;overflow:hidden;border:.125rem solid transparent;box-sizing:border-box;-webkit-mask:linear-gradient(transparent calc(50% - 1rem),#000 calc(50% - 1rem),#000 calc(50% + 1rem),transparent calc(50% + 1rem));mask:linear-gradient(transparent calc(50% - 1rem),#000 calc(50% - 1rem),#000 calc(50% + 1rem),transparent calc(50% + 1rem))}.t-cell[_ngcontent-%COMP%]:first-child{border-inline-start-color:transparent!important}.t-cell[_ngcontent-%COMP%]:last-child{border-inline-end-color:transparent!important}.t-cell[_ngcontent-%COMP%]:before, .t-cell[_ngcontent-%COMP%]:after{position:absolute;top:0;left:0;bottom:0;right:0;content:\"\";z-index:-1;border-radius:var(--tui-radius-m)}.t-cell[_ngcontent-%COMP%]:after{-webkit-mask:url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 12 32\"><path d=\"M0.2856 0L0.6763 0C2.9265 0 4.9876 1.259 6.0147 3.2611L10.2442 11.5048C11.5301 14.0113 11.5683 16.9754 10.3472 19.5141L5.9766 28.6007C4.9772 30.6786 2.8754 32 0.5696 32H0.285645V0Z\"></path></svg>') right / .75rem 100% no-repeat,linear-gradient(#000,#000) left / calc(100% - .7rem) 100% no-repeat;mask:url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 12 32\"><path d=\"M0.2856 0L0.6763 0C2.9265 0 4.9876 1.259 6.0147 3.2611L10.2442 11.5048C11.5301 14.0113 11.5683 16.9754 10.3472 19.5141L5.9766 28.6007C4.9772 30.6786 2.8754 32 0.5696 32H0.285645V0Z\"></path></svg>') right / .75rem 100% no-repeat,linear-gradient(#000,#000) left / calc(100% - .7rem) 100% no-repeat}.t-cell[data-range][_ngcontent-%COMP%]:before{background:var(--tui-background-neutral-1)}._picking[_nghost-%COMP%]   .t-cell[data-range][_ngcontent-%COMP%]:before{background:var(--tui-background-neutral-1-hover)}.t-cell[data-range=middle][_ngcontent-%COMP%]{border-color:var(--tui-background-neutral-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=middle][_ngcontent-%COMP%]{border-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=middle][_ngcontent-%COMP%]:not(:first-child):before{border-top-left-radius:0;border-bottom-left-radius:0}.t-cell[data-range=middle][_ngcontent-%COMP%]:not(:last-child):before{border-top-right-radius:0;border-bottom-right-radius:0}.t-cell[data-range=start][_ngcontent-%COMP%]{border-inline-end-color:var(--tui-background-neutral-1);color:var(--tui-text-primary-on-accent-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=start][_ngcontent-%COMP%]{border-inline-end-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=start][_ngcontent-%COMP%]:not(:last-child):before{right:-1rem}.t-cell[data-range=start][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1)}.t-cell[data-range=end][_ngcontent-%COMP%]{border-inline-start-color:var(--tui-background-neutral-1);color:var(--tui-text-primary-on-accent-1)}._picking[_nghost-%COMP%]   .t-cell[data-range=end][_ngcontent-%COMP%]{border-inline-start-color:var(--tui-background-neutral-1-hover)}.t-cell[data-range=end][_ngcontent-%COMP%]:not(:first-child):before{left:-1rem}.t-cell[data-range=end][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1);transform:scaleX(-1)}.t-cell[data-range=active][_ngcontent-%COMP%]{color:var(--tui-text-primary-on-accent-1)}.t-cell[data-range=active][_ngcontent-%COMP%]:after{background:var(--tui-background-accent-1);-webkit-mask:none;mask:none}.t-cell_disabled[_ngcontent-%COMP%]{opacity:var(--tui-disabled-opacity);pointer-events:none}.t-cell_today[_ngcontent-%COMP%]{text-decoration:underline;text-underline-offset:.25rem}@media (hover: hover) and (pointer: fine){.t-cell[_ngcontent-%COMP%]:hover:not([data-range=start]):not([data-range=end]):before{background:var(--tui-background-neutral-1-hover)}.t-cell[data-range=start][_ngcontent-%COMP%]:hover:after, .t-cell[data-range=end][_ngcontent-%COMP%]:hover:after, .t-cell[data-range=active][_ngcontent-%COMP%]:hover:after{background:var(--tui-background-accent-1-hover)}}[_nghost-%COMP%]{display:block;inline-size:16rem}.t-cell[_ngcontent-%COMP%]{inline-size:4rem;border-block-start-width:.5rem;border-block-end-width:.5rem}"],
        changeDetection: 0
      });
    }
  }
  return TuiCalendarYear;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiCalendar = /*#__PURE__*/(() => {
  class TuiCalendar {
    constructor() {
      this.day = null;
      this.view = 'month';
      this.month = TuiMonth.currentLocal();
      this.disabledItemHandler = TUI_FALSE_HANDLER;
      this.min = TUI_FIRST_DAY;
      this.max = TUI_LAST_DAY;
      this.minViewedMonth = TUI_FIRST_DAY;
      this.maxViewedMonth = TUI_LAST_DAY;
      this.hoveredItem = null;
      this.showAdjacent = true;
      this.markerHandler = null;
      this.dayClick = new EventEmitter();
      this.monthChange = new EventEmitter();
      this.hoveredItemChange = new EventEmitter();
      this.disabledItemHandlerMapper = (disabledItemHandler, min, max) => item => item.dayBefore(min) || item.dayAfter(max) || disabledItemHandler(item);
    }
    set value(value) {
      this.day = value;
      if (this.showAdjacent && value instanceof TuiDay && value.daySameOrBefore(TUI_LAST_DISPLAYED_DAY)) {
        this.month = value;
      }
    }
    set initialView(view) {
      this.view = view;
    }
    get value() {
      return this.day;
    }
    onPaginationValueChange(month) {
      this.updateViewedMonth(month);
    }
    onDayClick(day) {
      this.dayClick.emit(day);
    }
    onHoveredItemChange(day) {
      this.updateHoveredDay(day);
    }
    get computedMin() {
      return this.min ?? TUI_FIRST_DAY;
    }
    get computedMax() {
      return this.max ?? TUI_LAST_DAY;
    }
    get computedMinViewedMonth() {
      const min = this.computedMin;
      const minViewed = this.minViewedMonth ?? TUI_FIRST_DAY;
      return minViewed.monthSameOrAfter(min) ? minViewed : min;
    }
    get computedMaxViewedMonth() {
      const max = this.computedMax;
      const maxViewed = this.maxViewedMonth ?? TUI_LAST_DAY;
      return maxViewed.monthSameOrBefore(max) ? maxViewed : max;
    }
    get isInYearView() {
      return this.view === 'year';
    }
    onPaginationYearClick() {
      this.view = 'year';
    }
    onPickerYearClick(year) {
      this.view = 'month';
      this.updateViewedMonth(new TuiMonth(year, this.month.month));
    }
    updateViewedMonth(month) {
      if (this.month.monthSame(month)) {
        return;
      }
      this.month = month;
      this.monthChange.emit(month);
    }
    updateHoveredDay(day) {
      if (tuiNullableSame(this.hoveredItem, day, (a, b) => a.daySame(b))) {
        return;
      }
      this.hoveredItem = day;
      this.hoveredItemChange.emit(day);
    }
    static {
      this.ɵfac = function TuiCalendar_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiCalendar)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiCalendar,
        selectors: [["tui-calendar"]],
        inputs: {
          month: "month",
          disabledItemHandler: "disabledItemHandler",
          min: "min",
          max: "max",
          minViewedMonth: "minViewedMonth",
          maxViewedMonth: "maxViewedMonth",
          hoveredItem: "hoveredItem",
          showAdjacent: "showAdjacent",
          markerHandler: "markerHandler",
          value: "value",
          initialView: "initialView"
        },
        outputs: {
          dayClick: "dayClick",
          monthChange: "monthChange",
          hoveredItemChange: "hoveredItemChange"
        },
        decls: 3,
        vars: 2,
        consts: [["calendar", ""], ["automation-id", "tui-calendar__scrollbar", "class", "t-scrollbar", 4, "ngIf", "ngIfElse"], ["automation-id", "tui-calendar__scrollbar", 1, "t-scrollbar"], ["automation-id", "tui-calendar__year", 3, "yearClick", "initialItem", "max", "min", "value"], ["automation-id", "tui-calendar__pagination", 1, "t-pagination", 3, "valueChange", "yearClick", "max", "min", "value"], ["automation-id", "tui-calendar__calendar", 3, "dayClick", "hoveredItemChange", "disabledItemHandler", "hoveredItem", "markerHandler", "month", "showAdjacent", "value"]],
        template: function TuiCalendar_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, TuiCalendar_tui_scrollbar_0_Template, 2, 4, "tui-scrollbar", 1)(1, TuiCalendar_ng_template_1_Template, 3, 14, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
          }
          if (rf & 2) {
            const calendar_r4 = i0.ɵɵreference(2);
            i0.ɵɵproperty("ngIf", ctx.isInYearView)("ngIfElse", calendar_r4);
          }
        },
        dependencies: [NgIf, TuiCalendarSheet, TuiCalendarSpin, TuiCalendarYear, TuiMapperPipe, TuiScrollbar],
        styles: ["[_nghost-%COMP%]{display:block;min-block-size:18.25rem;inline-size:15.75rem;padding:1rem 1.125rem;box-sizing:content-box}.t-scrollbar[_ngcontent-%COMP%]{block-size:18.25rem;inline-size:16.875rem}.t-pagination[_ngcontent-%COMP%]{margin-bottom:1rem}"],
        changeDetection: 0
      });
    }
  }
  return TuiCalendar;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_CALENDAR_SHEET_DEFAULT_OPTIONS, TUI_CALENDAR_SHEET_OPTIONS, TuiCalendar, TuiCalendarSheet, TuiCalendarSpin, TuiCalendarYear, tuiCalendarSheetOptionsProvider };
